import { GLOBAL_CONFIG } from '~/configs/environment/environment';
import { OFetchRequestOptions, useOFetch } from '../../requests';
import { CustomFetchContext } from '../../requests/types';
import { ApiResponse } from '../../types';
import {
  DailyPunchCardResponse,
  ChapterPracticeResponse,
  ExamResultResponse,
  ExamDetailResponse,
  ScoreRankListResponse,
  CountRankListResponse,
  ExamRoleResponse,
  ExamInfoTokenResponse,
  ErrorCountResponse,
  QuestionInfoResponse,
  PracticeQuestionItem,
  SubmitExamAnswerResponse,
  ListExamInfoResponse,
  ListExamResultResponse,
  ListExamAnalyzeResponse,
  ChapterExamInfoResponse,
  ChapterExamAnalyzeResponse,
  ExamTimeResponse,
  ExamNoteListResponse,
  CampListResponse,
  ExamScoreInfoResponse,
  ExamExamineInfoResponse,
  IsShowAiResponse,
  IsShowAiTypeEnum,
  AiReviewGenEnum,
  AiReviewTypeEnum,
  GenerateAiQuestionReviewResponse,
  AiQuestionReviewResultResponse,
  AddNoteResponse,
  AddNotesResponse,
  AddNoteParams,
  IsAdoptTypeEnum,
  DailyPunchTypeEnum,
  KnowledgePracticeResponse,
  KnowledgePracticeAnalyzeResponse,
} from './types';

// 获取考试详情
export const fetchExamDetail = async (
  query: {
    id: string;
    version?: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ExamDetailResponse>> => {
  return await useOFetch<ExamDetailResponse>(
    '/exam/view',
    {
      method: 'GET',
      query,
      ...options,
    },
    fetchContext
  );
};

// 获取分数排行榜
export const fetchScoreRankList = async (
  query: {
    id: string;
    versionId?: number;
    versionYear?: number;
    page: number;
    pageSize: number;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ScoreRankListResponse>> => {
  return await useOFetch<ScoreRankListResponse>(
    '/exam/get-exam-score-tanking',
    {
      method: 'GET',
      query,
      ...options,
    },
    fetchContext
  );
};

// 获取考试次数排行榜
export const fetchCountRankList = async (
  query: {
    id: string;
    versionId?: number;
    versionYear?: number;
    page: number;
    pageSize: number;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<CountRankListResponse>> => {
  return await useOFetch<CountRankListResponse>(
    '/exam/get-current-answer',
    {
      method: 'GET',
      query,
      ...options,
    },
    fetchContext
  );
};

// 获取考试权限
export const fetchExamRole = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ExamRoleResponse>> => {
  return await useOFetch<ExamRoleResponse>(
    '/exam/examrole',
    {
      method: 'POST',
      body,
      ...options,
    },
    fetchContext
  );
};

// 获取试卷详情token
export const fetchExamInfoToken = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ExamInfoTokenResponse>> => {
  return await useOFetch<ExamInfoTokenResponse>(
    '/ajax/get-exam-token',
    {
      method: 'POST',
      body,
      ...options,
    },
    fetchContext
  );
};

// 获取试卷详情
export const fetchExamInfo = async (
  query: {
    id: string;
    tradeNumber?: string;
    exchangeType?: string;
    token: string;
    time?: string;
    version?: string;
    codeId?: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/ajax/exam-detail-new',
    {
      method: 'GET',
      query,
      ...options,
    },
    fetchContext
  );
};

// 获取试卷解析
export const fetchExamAnalyze = async (
  query: {
    id: string;
    token: string;
    time: string;
    number: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/ajax/exam-analyze',
    {
      method: 'GET',
      query,
      ...options,
    },
    fetchContext
  );
};

// 保存进度
export const saveExamAnswerProgress = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/ajax/save-progress',
    {
      method: 'POST',
      body,
      ...options,
    },
    fetchContext
  );
};

// 提交试卷
export const submitExamAnswer = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<SubmitExamAnswerResponse>> => {
  return await useOFetch<SubmitExamAnswerResponse>(
    '/ajax/submit',
    {
      method: 'POST',
      body,
      ...options,
    },
    fetchContext
  );
};

// 获取试卷详情（模拟考场，历年试题）
export const fetchListExamInfo = async (
  query: {
    id: string;
    needAnswer?: number; // 是否需要answer正确答案--（练习模式需要-展示答题卡保存题目的正确回显）
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ListExamInfoResponse>> => {
  return await useOFetch<ListExamInfoResponse>(
    '/list/detail',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 保存进度（模拟考场，历年试题）
export const saveListExamAnswerProgress = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/ajax/save-progress',
    {
      method: 'POST',
      body,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 提交试卷（模拟考场，历年试题）
export const submitListExamAnswer = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/list/submit',
    {
      method: 'POST',
      body,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 获取考试结果（模拟考场，历年试题）
export const fetchListExamResult = async (
  query: {
    id: string;
    number?: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ListExamResultResponse>> => {
  return await useOFetch<ListExamResultResponse>(
    '/list/report',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 获取考试解析（模拟考场，历年试题）
export const fetchListExamAnalyze = async (
  query: {
    id: string;
    number?: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ListExamAnalyzeResponse>> => {
  return await useOFetch<ListExamAnalyzeResponse>(
    '/list/analyze',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 获取章节练习详情
export const fetchChapterExamInfo = async (
  query: {
    id: string;
    userSubject?: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ChapterExamInfoResponse>> => {
  return await useOFetch<ChapterExamInfoResponse>(
    '/chapter/section-questions',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 章节练习保存进度
export const saveChapterExamAnswerProgress = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/chapter/save-section-progress',
    {
      method: 'POST',
      body,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 章节练习提交答案
export const submitChapterExamAnswer = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<{ submitId: string }>> => {
  return await useOFetch<{ submitId: string }>(
    '/chapter/task-submit',
    {
      method: 'POST',
      body,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 获取章节练习解析
export const fetchChapterExamAnalyze = async (
  query: {
    id: string;
    submitId: string;
    type: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ChapterExamAnalyzeResponse>> => {
  return await useOFetch<ChapterExamAnalyzeResponse>(
    '/chapter/section-question-analyze',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 获取题目已纠错的次数
export const fetchErrorCount = async (
  query: {
    questionId: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ErrorCountResponse>> => {
  return await useOFetch<ErrorCountResponse>(
    '/user/error-count',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 提交题目纠错信息
export const submitError = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/user/error-submit',
    {
      method: 'POST',
      body,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 获取考试解析
export const fetchQuestionAnalyzeById = async (
  query: {
    id: number;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/exam/get-question-analyze',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 获取题目信息
export const fetchQuestionInfoById = async (
  query: {
    id: number;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<QuestionInfoResponse>> => {
  return await useOFetch<QuestionInfoResponse>(
    '/exam/get-question',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 获取每日打卡题目
export const fetchDailyPunchCardQuestion = async (
  query: {
    type?: DailyPunchTypeEnum;
    id?: string;
    userSubject?: string;
    date?: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<PracticeQuestionItem[]>> => {
  return await useOFetch<PracticeQuestionItem[]>(
    '/practice/everyday',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 提交每日打卡
export const submitDailyPunchCardAnswer = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/practice/every-submit',
    {
      method: 'POST',
      body,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 每日打卡结果
export const fetchDailyPunchCardResult = async (
  query: {
    date: string;
    type?: string;
    id?: string;
    userSubject?: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<DailyPunchCardResponse>> => {
  return await useOFetch<DailyPunchCardResponse>(
    'practice/every-report',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 获取连续打卡天数
export const fetchContinueDailyPunchCount = async (
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<{ continueSign: number; totalSignCount: number }>> => {
  return await useOFetch<{ continueSign: number; totalSignCount: number }>(
    'practice/continue-sign',
    {
      method: 'GET',
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 获取活动练习打卡
export const fetchContinueDailyPunchActCount = async (
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<number>> => {
  return await useOFetch<number>(
    'practice/continue-sign-act',
    {
      method: 'GET',
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 章节练习结果
export const fetchChapterPracticeResult = async (
  query: {
    submitId: string;
    id: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ChapterPracticeResponse>> => {
  return await useOFetch<ChapterPracticeResponse>(
    'chapter/practice-res',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

export const resetChapterPracticeLog = async (
  query: {
    chapterId: string;
    sectionId: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<void>> => {
  return await useOFetch<void>(
    'chapter/reset-practice-log',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 试卷考试结果
export const fetchExamResult = async (
  query: {
    id: string;
    number?: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ExamResultResponse>> => {
  return await useOFetch<ExamResultResponse>(
    'ajax/exam-report',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 收藏题目
export const addCollectQuestion = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/collection/add',
    {
      method: 'POST',
      body,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 取消收藏题目
export const removeCollectQuestion = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/collection/remove',
    {
      method: 'POST',
      body,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 小程序登录PC
export const miniProgramToPc = async (
  headers: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'user-token': string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/pub/mini2pc',
    {
      method: 'GET',
      headers,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 获取考试时间
export const getExamTime = async (
  query: {
    id?: string;
    version?: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ExamTimeResponse>> => {
  return await useOFetch<ExamTimeResponse>(
    '/ajax/once-time',
    {
      method: 'GET',
      query,
      ...options,
    },
    fetchContext
  );
};

// 获取结果页 训练营课程
export const getCampList = async (
  query: {
    userPage: string;
    userSubject: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<CampListResponse>> => {
  return await useOFetch<CampListResponse>(
    `${GLOBAL_CONFIG.envConfig.eduUrl}/center/live/mini-program/live-v3`,
    {
      method: 'GET',
      query,
      ...options,
    },
    fetchContext
  );
};

// 获取批注列表
export const getNoteList = async (
  query: {
    answerId: string;
    examAnswerDetailId: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ExamNoteListResponse>> => {
  return await useOFetch<ExamNoteListResponse>(
    '/note/get-note-list',
    {
      method: 'GET',
      query,
      ...options,
    },
    fetchContext
  );
};

// 添加批注
export const addNote = async (
  body: AddNoteParams,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<AddNoteResponse>> => {
  return await useOFetch<AddNoteResponse>(
    '/note/add-note',
    {
      method: 'POST',
      body,
      ...options,
    },
    fetchContext
  );
};

// 批量添加批注
export const addNotes = async (
  body: AddNoteParams[],
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<AddNotesResponse>> => {
  return await useOFetch<AddNotesResponse>(
    '/note/add-notes',
    {
      method: 'POST',
      body,
      ...options,
    },
    fetchContext
  );
};

// 删除批注
export const deleteNote = async (
  body: {
    id: string;
    noteContent?: string;
    version?: number;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/note/del-note',
    {
      method: 'POST',
      body,
      ...options,
    },
    fetchContext
  );
};

// 编辑批注
export const editNote = async (
  body: {
    id: string;
    noteContentReply: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/note/edit-note',
    {
      method: 'POST',
      body,
      ...options,
    },
    fetchContext
  );
};

// 获取阅卷分数
export const getScoreInfo = async (
  query: {
    id: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ExamScoreInfoResponse>> => {
  return await useOFetch<ExamScoreInfoResponse>(
    '/ajax/score-info',
    {
      method: 'GET',
      query,
      ...options,
    },
    fetchContext
  );
};

// 获取阅卷考试信息
export const getExamineInfo = async (
  query: {
    id: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ExamExamineInfoResponse>> => {
  return await useOFetch<ExamExamineInfoResponse>(
    '/ajax/examine-info',
    {
      method: 'GET',
      query,
      ...options,
    },
    fetchContext
  );
};

// 提交学习建议
export const submitLecturerProposal = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/lecturer/proposal',
    {
      method: 'POST',
      body,
      ...options,
    },
    fetchContext
  );
};

// 保存阅卷
export const submitLecturerRead = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/lecturer/read',
    {
      method: 'POST',
      body,
      ...options,
    },
    fetchContext
  );
};

// 是否ai批阅入口
export const isShowAi = async (
  query: {
    examId: number;
    type: IsShowAiTypeEnum;
    answerId?: string;
    version?: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<IsShowAiResponse>> => {
  return await useOFetch<IsShowAiResponse>(
    '/ai/is-show-ai',
    {
      method: 'GET',
      query,
      ...options,
    },
    fetchContext
  );
};

// ai批阅文档
export const generateAiQuestionReview = async (
  query: {
    examId: number;
    answerId: number;
    questionId: number;
    genType: AiReviewGenEnum;
    type: AiReviewTypeEnum;
    examAnswerDetailId: number;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<GenerateAiQuestionReviewResponse>> => {
  return await useOFetch<GenerateAiQuestionReviewResponse>(
    '/ai/gpt-stream',
    {
      method: 'GET',
      query,
      ...options,
      timeout: 60 * 1000,
    },
    fetchContext
  );
};

// 获取ai批阅文档内容
export const getAiQuestionReviewResult = async (
  query: {
    answerId: number;
    questionId: number;
    examId: number;
    aiType: AiReviewTypeEnum;
    examAnswerDetailId: number;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<AiQuestionReviewResultResponse>> => {
  return await useOFetch<AiQuestionReviewResultResponse>(
    '/ai/get-ai-result',
    {
      method: 'GET',
      query,
      ...options,
    },
    fetchContext
  );
};

// 采纳ai批注
export const changeNoteAdopt = async (
  body: {
    noteId: number;
    isAdopt: IsAdoptTypeEnum;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/note/add-adopt-note',
    {
      method: 'POST',
      body,
      ...options,
    },
    fetchContext
  );
};

// 提交问题到AI
// ofetch无法返回一个未被锁定的可读流，因此考虑采用fetch
// export const submitUserQuestionToAi = async (query: {
//   theme: string;
//   token: string;
// }): Promise<any> => {
//   return await oStreamFetch.raw('/ai/gpt-stream-user', {
//     method: 'GET',
//     query,
//   });
// };

let abortController: null | AbortController = null;

export const submitUserQuestionToAi = async (query: {
  theme: string;
  token: string;
  examId: number;
  version?: string;
}) => {
  if (abortController) {
    abortController = null;
  }

  abortController = new AbortController();

  return await fetch(`${GLOBAL_CONFIG.envConfig.origin}/napi/ai/gpt-stream-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...query }),
    signal: abortController.signal,
  });
};

// 关闭连接
export const closeStreamConnect = () => {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
};

// 获取知识点试卷数据
export const fetchKnowledgeExamInfo = async (
  query: {
    id: string;
    userSubject?: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<ChapterExamInfoResponse>> => {
  return await useOFetch<ChapterExamInfoResponse>(
    '/kpoint/question',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 知识点保存进度
export const saveKnowledgeExamAnswerProgress = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<string>> => {
  return await useOFetch<string>(
    '/kpoint/save-progress',
    {
      method: 'POST',
      body,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 知识点练习交卷
export const submitKnowledgeExamAnswer = async (
  body: FormData,
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<{ submitId: string }>> => {
  return await useOFetch<{ submitId: string }>(
    '/kpoint/submit',
    {
      method: 'POST',
      body,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 知识点练习结果
export const fetchKnowledgePracticeResult = async (
  query: {
    submitId: string;
    id: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<KnowledgePracticeResponse>> => {
  return await useOFetch<KnowledgePracticeResponse>(
    '/kpoint/practice-res',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};

// 知识点练习解析
export const fetchKnowledgePracticeAnalyze = async (
  query: {
    id: string;
    submitId: string;
    type: string;
  },
  options?: OFetchRequestOptions,
  fetchContext?: CustomFetchContext
): Promise<ApiResponse<KnowledgePracticeAnalyzeResponse>> => {
  return await useOFetch<KnowledgePracticeAnalyzeResponse>(
    '/kpoint/analyze',
    {
      method: 'GET',
      query,
      ...options,
      useHeaderMiniProgramSubjectId: true,
    },
    fetchContext
  );
};
